import type { StackProps } from '@chakra-ui/react';
import { Button, HStack, Link, Text, VStack } from '@chakra-ui/react';
import type { ExplicitAddress } from '@cronos-app/sdk';
import { MaskIcon } from '@ui/components/MaskIcon';
import { Num } from '@ui/components/Num';
import { PercentChange } from '@ui/components/Num/PercentChange';
import { UsdValue } from '@ui/components/Num/UsdValue';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { Token } from '@ui/config/token';
import { useBondingCurveExpectedReceiveAmountForSell } from '@ui/hooks/bondingCurve/useBondingCurveExpectedReceiveAmount';
import { useTokenBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenConfig } from '@ui/hooks/contracts/useTokenConfig';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { useWatchAsset } from '@ui/hooks/useWatchAsset';
import { Fraction } from 'bi-fraction';
import { useMemo } from 'react';

type TokenListRowProps = StackProps & {
  token: Token;
  tokenAddress?: ExplicitAddress;
};
export const TokenListRow = ({
  token,
  tokenAddress,
  ...props
}: TokenListRowProps) => {
  const chainLayer = useGlobalChainLayer();
  const { watchAsset, canWatch } = useWatchAsset();
  const tokenInfo = useTokenConfig({
    token,
    tokenAddress,
  });

  const balance = useTokenBalance({
    tokenAddress: tokenInfo?.address,
  }).data;

  const agentFunTokenPrice = useTokenPrice({
    token: Token.agentFun,
  }).data;

  const _expectedReceiveAmountOfAgentFun =
    useBondingCurveExpectedReceiveAmountForSell({
      amount: balance ?? Fraction.ZERO,
      tokenAddress: tokenAddress ?? '',
      enabled: !!tokenAddress && token === Token.Bonding,
    }).data;

  const expectedReceiveAmountOfAgentFun = useMemo(() => {
    if (token === Token.Bonding) {
      return _expectedReceiveAmountOfAgentFun;
    } else {
      return balance;
    }
  }, [_expectedReceiveAmountOfAgentFun, balance, token]);

  return (
    <HStack align="flex-start" py="12px" {...props}>
      <VStack gap="2px" alignItems="flex-start" maxW="50%">
        <HStack gap="4px">
          {tokenInfo?.Icon && <tokenInfo.Icon boxSize="16px" rounded="full" />}
          <Text textStyle="body2" lineClamp={1} wordBreak="break-all">
            {tokenInfo?.symbol}
          </Text>
          <PercentChange
            value={agentFunTokenPrice?.usd_price_change_24h}
            textStyle="caption"
          />
        </HStack>
        {canWatch && (
          <Button
            variant="tertiary"
            textStyle="body2Bold"
            onClick={() => {
              watchAsset({
                token,
              });
            }}
          >
            Add to wallet
          </Button>
        )}
      </VStack>
      <VStack align="flex-end" gap="2px" maxW="50%" ml="auto">
        <Num
          value={balance}
          textStyle="body2Bold"
          lineClamp={1}
          wordBreak="break-all"
        />
        <UsdValue
          prefix="~ $"
          value={expectedReceiveAmountOfAgentFun}
          price={agentFunTokenPrice?.usd_price}
          textStyle="body3"
          lineClamp={1}
          wordBreak="break-all"
        />
      </VStack>
      {token === Token.agentFun && balance?.lte(0) && (
        <Button
          asChild
          variant="tertiary"
          alignSelf="center"
          textStyle="body2Bold"
        >
          <Link href={EXTERNAL_LINK.swapAgentFun(chainLayer)} target="_blank">
            Buy <MaskIcon icon="url(/masks/arrow-outward.svg)" boxSize="16px" />
          </Link>
        </Button>
      )}
    </HStack>
  );
};
